import { DragEvent, ReactNode, useCallback, useRef, useState } from 'react';

import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { cn } from '@commenda/utils';

import { toast } from '@commenda/kakashi';
import { ChatUploadHelper } from './ChatUploadHelper';
import { useChatFolderUpload } from './useChatFolderUpload';

interface ChatDropZoneProps {
  children: ReactNode;
  onFilesDropped: (files: CommendaAiUploadFile[]) => void;
  /** Layout classes for the wrapper, so it can stand in for the element it replaces. */
  className?: string;
}

/** Makes a whole chat surface a drop target, not just the composer.
 *
 * Lives in the shared chat rather than the panel because both surfaces reuse the same chat: the full page
 * at /agent and the side panel both wrap their content in this, so a file dropped anywhere over
 * either one is picked up. Aiming at the composer is a needless precision task when the entire
 * surface belongs to one conversation.
 *
 * The overlay only appears once the drag actually carries files, so dragging selected text over the
 * chat does not trigger it. */
export const ChatDropZone = ({
  children,
  onFilesDropped,
  className,
}: ChatDropZoneProps) => {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const canUploadFolders = useChatFolderUpload();
  // dragenter and dragleave fire for every child element the pointer crosses, so a boolean alone
  // flickers. Counting entries against leaves is what keeps the overlay steady.
  const dragDepthRef = useRef(0);

  const carriesFiles = (event: DragEvent<HTMLDivElement>) =>
    event.dataTransfer.types.includes('Files');

  const resetDrag = () => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
  };

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) resetDrag();
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    // Without this the browser navigates to the file instead of letting us handle the drop.
    event.preventDefault();
  }, []);

  // Capture phase, so the overlay clears on every drop, including one the composer claims for
  // itself and stops before it bubbles back here.
  const onDropCapture = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (carriesFiles(event)) resetDrag();
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      ChatUploadHelper.readDrop(event.dataTransfer, canUploadFolders)
        .then(onFilesDropped)
        .catch(() => toast.error('Could not read the dropped files.'));
    },
    [onFilesDropped, canUploadFolders]
  );

  return (
    <div
      className={cn('relative', className)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDropCapture={onDropCapture}
      onDrop={onDrop}
    >
      {children}
      {isDraggingFiles && (
        <div
          // pointer-events-none so the overlay cannot swallow the drop it is announcing.
          // bg-light at 80% over a backdrop blur: the blur softens what is behind rather than
          // hiding it, so the chat stays recognisable while the drop copy is unmistakably on top.
          className="pointer-events-none absolute inset-none z-20 flex flex-col items-center justify-center gap-md bg-bg-light/80 px-xl text-center backdrop-blur-sm"
          aria-hidden="true"
        >
          <p className="display-xs-bold text-text-primary">Add anything</p>
          <p className="txt-small-medium text-text-primary">
            {canUploadFolders
              ? 'Drop files or a folder here to add them to the conversation'
              : 'Drop a file here to add it to the conversation'}
          </p>
          {/* Only what the stack actually reads: images and PDFs reach the model as content blocks,
              and DOCX, RTF, TXT and MD are text-extracted server-side. CSV and XLSX are neither. */}
          <p className="txt-extra-small-medium text-text-placeholder">
            PNG, JPG, PDF, DOCX, RTF, TXT or MD
          </p>
        </div>
      )}
    </div>
  );
};
